/*
 * StackCard
 * @module components
 * @flow
 */

import React, { Fragment } from 'react';
import styled, { keyframes, css } from 'styled-components/macro';
import styledContainerQuery from 'styled-container-query';
import { palette, key } from 'styled-theme';
import { ifProp, prop } from 'styled-tools';
import * as R from 'ramda';

import { AnimatedRain, Block, Tooltip, DataValue } from '~/components';
import imageCoinSpriteSource from '@/images/coin-sprite.png';

import { StackScore, type StackScoreProps } from '../StackScore';

export type StackCardProps = $Shape<StackScoreProps> & {
  totalScoreValue: $PropertyType<StackScoreProps, 'scoreValue'>,
  totalScorePtc: $PropertyType<StackScoreProps, 'scorePtc'>,
  totalScorePercentageStickerTooltip?: React$Node,
  cumulateScoreValue: $PropertyType<StackScoreProps, 'scoreValue'>,
  cumulateScorePtc: $PropertyType<StackScoreProps, 'scorePtc'>,
  cumulateScoreTooltip?: React$Node,
  cumulateScorePercentageStickerTooltip?: React$Node,
  deltaPtc: number,
  isScoreVisible?: boolean,
  isVertical?: boolean,
  isVisibleDelta?: boolean,
  deltaTooltip?: React$Node,
  waved?: boolean,
  rained?: boolean,
  bottomContent?: React$Node,
  transparent?: boolean,
  currentTitle?: React$Node,
};

const WAVE = {
  MIN_HEIGHT_LARGE_SCREEN: 20,
  MIN_HEIGHT_MEDIUM_SCREEN: 20,
  MIN_HEIGHT_SMALL_SCREEN: 37,
  MIN_HEIGHT_SMALL_SCREEN_MOBILE: 105,
  MIN_HEIGHT_PTC_LARGE_SCREEN: 37,
  MIN_HEIGHT_PTC_MEDIUM_SCREEN: 37,
  MIN_HEIGHT_PTC_SMALL_SCREEN: 43,
  MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE: 105,
  MIN_HEIGHT_BOTTOM_CONTENT: 50,
};

const TOOLTIP_HOLDER = {
  MAX_HEIGHT: 80,
  MIN_HEIGHT_LARGE_SCREEN: 23,
  MIN_HEIGHT_MEDIUM_SCREEN: 23,
  MIN_HEIGHT_SMALL_SCREEN: 28,
  MIN_HEIGHT_PTC_LARGE_SCREEN: 35,
  MIN_HEIGHT_PTC_MEDIUM_SCREEN: 35,
  MIN_HEIGHT_PTC_SMALL_SCREEN: 43,
  MIN_HEIGHT_BOTTOM_CONTENT: 50,
};

const backgroundImage = ({ variant }: StackCardProps) =>
  key(`flasks.${variant}[2]`);

const foregroundColor = ({ variant, reverse, palette: p }: StackCardProps) =>
  reverse
    ? palette('grayscale', 0, R.equals('secondary', p))
    : key(`flasks.${variant}[0]`);

const getWaveTransform = ({
  device,
  height,
  minHeight,
  minHeightMobile,
  minHeightPtc,
  minHeightPtcMobile,
  isVertical,
  isVisiblePtc,
  isVisibleDelta,
  transparent,
  bottomContent,
}) => {
  const isMobile = device !== 'desktop';

  const defaultHeight = bottomContent
    ? WAVE.MIN_HEIGHT_BOTTOM_CONTENT
    : isVertical && isVisiblePtc
    ? isMobile
      ? minHeightPtcMobile
      : minHeightPtc
    : isMobile
    ? minHeightMobile
    : minHeight;

  const calculatedHeight = !transparent
    ? R.cond([
        [R.gt(defaultHeight), R.always(defaultHeight)],
        [R.T, R.identity],
      ])(height)
    : 0;

  const multiplier = isVisibleDelta ? 0.3 : 0.4;

  let verticalShilf = calculatedHeight * multiplier + 64;

  if (isMobile && verticalShilf > 101) {
    verticalShilf = 101;
  }

  return `-50%, -${calculatedHeight ? verticalShilf : 50}%`;
};

const rotate360 = props => {
  const animation = `
    from {
      transform: translate(${getWaveTransform(props)}) rotate(0deg);
    }
    to {
      transform: translate(${getWaveTransform(props)}) rotate(360deg);
    }
  `;

  return keyframes`${animation}`;
};

const getTooltipHolderHeight = ({
  device,
  height,
  maxHeight,
  minHeight,
  minHeightMobile,
  minHeightPtc,
  minHeightPtcMobile,
  isVertical,
  isVisiblePtc,
  bottom,
  bottomContent,
}) => {
  const isMobile = device !== 'desktop';

  const defaultHeight = !bottom
    ? 100 - maxHeight
    : bottomContent
    ? TOOLTIP_HOLDER.MIN_HEIGHT_BOTTOM_CONTENT
    : isVertical && isVisiblePtc
    ? isMobile
      ? minHeightPtcMobile
      : minHeightPtc
    : isMobile
    ? minHeightMobile
    : minHeight;

  const calculatedHeight = `${R.cond([
    [R.lt(maxHeight), R.always(maxHeight)],
    [R.gt(defaultHeight), R.always(defaultHeight)],
    [R.T, R.identity],
  ])(height)}%`;

  return calculatedHeight;
};

const getWaveTransformLargeScreen = props =>
  getWaveTransform({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_LARGE_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_LARGE_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const getWaveTransformMediumScreen = props =>
  getWaveTransform({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_MEDIUM_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_MEDIUM_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const getWaveTransformSmallScreen = props =>
  getWaveTransform({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_SMALL_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const rotate360LargeScreen = props =>
  rotate360({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_LARGE_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_LARGE_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const rotate360MediumScreen = props =>
  rotate360({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_MEDIUM_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_MEDIUM_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const rotate360SmallScreen = props =>
  rotate360({
    ...props,
    minHeight: WAVE.MIN_HEIGHT_SMALL_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const getTooltipHolderHeightLargeScreen = props =>
  getTooltipHolderHeight({
    ...props,
    maxHeight: TOOLTIP_HOLDER.MAX_HEIGHT,
    minHeight: TOOLTIP_HOLDER.MIN_HEIGHT_LARGE_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: TOOLTIP_HOLDER.MIN_HEIGHT_PTC_LARGE_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const getTooltipHolderHeightMediumScreen = props =>
  getTooltipHolderHeight({
    ...props,
    maxHeight: TOOLTIP_HOLDER.MAX_HEIGHT,
    minHeight: TOOLTIP_HOLDER.MIN_HEIGHT_MEDIUM_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: TOOLTIP_HOLDER.MIN_HEIGHT_PTC_MEDIUM_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const getTooltipHolderHeightSmallScreen = props =>
  getTooltipHolderHeight({
    ...props,
    maxHeight: TOOLTIP_HOLDER.MAX_HEIGHT,
    minHeight: TOOLTIP_HOLDER.MIN_HEIGHT_SMALL_SCREEN,
    minHeightMobile: WAVE.MIN_HEIGHT_SMALL_SCREEN_MOBILE,
    minHeightPtc: TOOLTIP_HOLDER.MIN_HEIGHT_PTC_SMALL_SCREEN,
    minHeightPtcMobile: WAVE.MIN_HEIGHT_PTC_SMALL_SCREEN_MOBILE,
  });

const StackCardFlaskWrapper = styled(Block).attrs(() => ({
  elevation: 2,
  className: 'react-stackcard-flask',
}))`
  position: relative;
  display: flex;
  margin-top: 9px;
  padding: ${ifProp({ device: 'mobile' }, '4px 15px', '19px 15px 17px')};
  height: ${ifProp('waved', '405px', '115px')};
  align-items: flex-end;
  justify-content: ${ifProp('isVisiblePtc', 'space-between', 'center')};
  overflow: hidden;
`;

const StackCardCurrentTitle = styled.div.attrs(() => ({
  className: 'react-stack-card-title',
}))`
  position: absolute;
  top: 9px;
  left: 15px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

const StackCardDeltaValue = styled.div`
  position: absolute;
  top: ${ifProp({ device: 'mobile' }, '4px', '21px')};
  left: 0;
  right: 0;
  text-align: center;
  font-size: ${ifProp({ device: 'mobile' }, '18px', '16px')};
  line-height: ${ifProp({ device: 'mobile' }, '20px', 1)};
  font-weight: 600;
  color: ${foregroundColor};
`;

const StackCardWrapper = styledContainerQuery.div`
  width: 160px;

  &:container(max-width: 128px) {
    ${/* sc-selector */ StackCardFlaskWrapper} {
      height: ${ifProp('waved', '324px', '115px')};
    }

    ${/* sc-selector */ StackCardCurrentTitle},
    ${/* sc-selector */ StackCardDeltaValue} {
      font-size: 14px;
    }
  }

  &:container(max-width: 103px) {
    ${/* sc-selector */ StackCardFlaskWrapper} {
      height: ${ifProp(
        'waved',
        ifProp({ device: 'mobile' }, '110px', '260px'),
        '115px',
      )};
      padding-left: 10px;
      padding-right: 10px;
    }
  }
`;

const StackCardBackground = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background-image: ${backgroundImage};

  ${ifProp(
    'transparent',
    css`
      height: 0;
    `,
    css`
      height: ${ifProp('loading', 0, '100%')};
      transition: height ${prop('waveSpeed', '1.5s')} ease-out;
      transition-delay: ${prop('delayAnimation', '0s')} !important;
    `,
  )}
`;

const StackCardAnimatedBackground = styledContainerQuery(StackCardBackground)`
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 1000px;
    height: 1000px;
    top: 0;
    left: 50%;
    transform: translate(${getWaveTransformLargeScreen});
    animation-delay: ${prop('delayAnimation', '0s')} !important;
  }

  &::before {
    border-radius: 45%;
    background: rgba(255, 255, 255, 1);
    animation: ${rotate360LargeScreen} 10s linear infinite;
  }

  &::after {
    border-radius: 40%;
    background: rgba(255, 255, 255, 0.5);
    animation: ${rotate360LargeScreen} 12s linear infinite;
  }

    &:container(max-width: 128px) {
      &::before,
      &::after {
        width: 800px;
        height: 800px;
        transform: translate(${getWaveTransformMediumScreen});
      }

      &::before {
        animation: ${rotate360MediumScreen} 10s linear infinite;
      }

      &::after {
        animation: ${rotate360MediumScreen} 12s linear infinite;
      }
    }
  }

  &:container(max-width: 103px) {
    &::before,
    &::after {
      transform: translate(${getWaveTransformSmallScreen});
    }

    &::before {
      animation: ${rotate360SmallScreen} 10s linear infinite;
    }

    &::after {
      animation: ${rotate360SmallScreen} 12s linear infinite;
    }
`;

const StackCardTooltipHolder = styledContainerQuery.div`
  position: absolute;
  left: 0;
  right: 0;
  ${ifProp(
    'bottom',
    css`
      bottom: 0;
      z-index: 1;
    `,
    css`
      top: 0;
    `,
  )};
  height: ${getTooltipHolderHeightLargeScreen};

  &:container(max-width: 128px) {
    height: ${getTooltipHolderHeightMediumScreen};
  }

  &:container(max-width: 103px) {
    height: ${getTooltipHolderHeightSmallScreen};
  }
`;

const StackScoreWrapperQuery = styledContainerQuery.div`
  width: 100%;

  &:container(max-width: 128px) {
    ${ifProp(
      { device: 'desktop' },
      css`
        height: 42px;
      `,
    )}

    .react-stack-score-value,
    .react-percentage-sticker {
      font-size: ${ifProp({ device: 'mobile' }, '20px', '14px')};
    }

    .react-percentage-sticker {
      width: 32px;
      height: 32px;

      sub {
        font-size: 10px;
      }
    }
  }

  &:container(max-width: 103px) {
    .react-percentage-sticker {
      ${ifProp(
        'isVertical',
        css`
          margin-bottom: 10px;
        `,
      )}

      sub {
        font-size: 8px;
      }
    }
  }
`;

/**
 * StackCard React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {number} [props.totalScoreValue = 0] - total score value
 * @param {number} [props.totalScorePtc] - total percents value
 * @param {number} [props.cumulateScoreValue = 0] - cumulate score value
 * @param {number} [props.cumulateScorePtc = 0] - cumulate percents value
 * @param {React$Node} [props.cumulateScoreTooltip] - cumulate percents tooltip
 * @param {React$Node} [props.deltaTooltip] - delta value tooltip
 * @param {string} [props.title] - show title instead of totalScoreValue
 * @param {string} [props.subtitle] - show subtitle
 * @param {boolean} [props.waved] - show animated wave
 * @param {boolean} [props.rained] - show animated coins rain
 * @param {boolean} [props.isScoreVisible] - score visibility flag
 * @param {boolean} [props.isVertical] - vertical alignment flag
 * @param {boolean} [props.isVisibleDelta] - show delta value
 * @param {boolean} [props.isVisiblePtc] - show percents
 * @param {React$Node} [props.bottomContent] - optional bottom content instead of values
 * @param {'one' | 'two' | 'three' | 'four' | 'five' | 'six' | 'seven' | 'eight'} [props.variant = 'one'] - colors variant
 * @param {string} [props.palette = 'primary'] - current button palette
 * @example <caption>Simple usage</caption>
 * <StackCard scoreValue={123564} rewardValue={45} name="Yann Russell" src="http://i.pravatar.cc/150?img=47" />
 * @returns {React.Node<*>} React-component
 */
export const StackCard = ({
  title,
  subtitle,
  totalScoreValue,
  totalScorePtc,
  totalScorePercentageStickerTooltip,
  cumulateScoreValue,
  cumulateScorePtc,
  isScoreVisible,
  isVertical,
  isVisiblePtc,
  isVisibleDelta,
  waved,
  rained,
  cumulateScoreTooltip,
  cumulateScorePercentageStickerTooltip,
  deltaTooltip,
  deltaValue,
  deltaPtc,
  device,
  bottomContent,
  transparent,
  currentTitle,
  variant,
  calculateWaveHeight,
  delayAnimation,
  ...rest
}: StackCardProps) => (
  <StackCardWrapper
    testid="stackcard"
    className="react-stackcard"
    waved={waved}
    device={device}
  >
    {isScoreVisible && (
      <StackScoreWrapperQuery>
        <StackScore
          title={title}
          value={totalScoreValue}
          valuePtc={totalScorePtc}
          variant={variant}
          isVisiblePtc={isVisiblePtc}
          tooltip={totalScorePercentageStickerTooltip}
          device={device}
        />
      </StackScoreWrapperQuery>
    )}
    <StackCardFlaskWrapper waved={waved} device={device}>
      {(() => {
        if (!waved) {
          return <StackCardBackground variant={variant} />;
        }

        const heightPtc = Math.min(
          calculateWaveHeight
            ? calculateWaveHeight()
            : 100 * (cumulateScoreValue / totalScoreValue) || 0,
          100,
        );

        return (
          <Fragment>
            <StackCardAnimatedBackground
              height={heightPtc}
              variant={variant}
              isVertical={isVertical}
              isVisiblePtc={isVisiblePtc}
              isVisibleDelta={isVisibleDelta}
              transparent={transparent}
              bottomContent={bottomContent}
              device={device}
              delayAnimation={delayAnimation}
            />
            {rained && <AnimatedRain imgSrc={imageCoinSpriteSource} />}
            {cumulateScoreTooltip && (
              <Tooltip placement="right" overlay={cumulateScoreTooltip}>
                <StackCardTooltipHolder
                  height={heightPtc}
                  isVertical={isVertical}
                  isVisiblePtc={isVisiblePtc}
                  bottomContent={bottomContent}
                  bottom
                />
              </Tooltip>
            )}
          </Fragment>
        );
      })()}
      {bottomContent || (
        <StackScoreWrapperQuery>
          <StackScore
            value={title ? totalScoreValue : cumulateScoreValue}
            valuePtc={cumulateScorePtc}
            variant={variant}
            isVertical={isVertical}
            isVisiblePtc={isVisiblePtc}
            isReversed
            tooltip={cumulateScorePercentageStickerTooltip}
            {...rest}
          />
        </StackScoreWrapperQuery>
      )}
      {(() => {
        if (currentTitle) {
          return (
            <StackCardCurrentTitle variant={variant}>
              {currentTitle}
            </StackCardCurrentTitle>
          );
        }

        if (!waved || !isVisibleDelta) {
          return null;
        }

        const value = Math.max(
          R.isNil(deltaValue)
            ? (totalScoreValue || 0) - (cumulateScoreValue || 0)
            : deltaValue,
          0,
        );

        return (
          <Fragment>
            <StackCardDeltaValue variant={variant} device={device}>
              <DataValue value={value} isEmptyVisible />
            </StackCardDeltaValue>
            {deltaTooltip && (
              <Tooltip placement="right" overlay={deltaTooltip}>
                <StackCardTooltipHolder
                  height={deltaPtc}
                  isVisiblePtc={isVisiblePtc}
                />
              </Tooltip>
            )}
          </Fragment>
        );
      })()}
    </StackCardFlaskWrapper>
  </StackCardWrapper>
);

StackCard.defaultProps = {
  variant: 'one',
  palette: 'primary',
  totalScoreValue: 0,
  cumulateScoreValue: 0,
  cumulateScorePtc: 0,
  isScoreVisible: true,
};
